/**
 * Outil « Arbre et carré de Bayes » — calculs (pur).
 *
 * Deux épreuves : A (ou Ā), puis B (ou B̄) selon le résultat de la première.
 * Les trois réglages sont des pourcentages ENTIERS (a = P(A), b1 = P_A(B),
 * b2 = P_Ā(B)) : toutes les probabilités se calculent en fractions exactes
 * (P_B(A) = a·b1 / (a·b1 + (100 − a)·b2)), et les effectifs sur 10 000
 * personnes sont entiers.
 */

import { rational, type ExactNumber } from '../../ui/exact';

export interface BayesParams {
  /** P(A) en % */
  a: number;
  /** P_A(B) en % */
  b1: number;
  /** P_Ā(B) en % */
  b2: number;
}

export type ScenarioId = 'screening' | 'factory' | 'free';

export interface Scenario {
  id: ScenarioId;
  label: string;
  /** lettre et sens de chaque événement */
  A: { letter: string; text: string };
  B: { letter: string; text: string };
  params: BayesParams;
  /** noms des réglages dans ce contexte */
  names: { a: string; b1: string; b2: string };
  /** noms sans « sachant » (collège, 2nde : le conditionnement est de 1re) */
  simpleNames?: { a: string; b1: string; b2: string };
}

export const SCENARIOS: readonly Scenario[] = [
  {
    id: 'screening',
    label: 'Dépistage',
    A: { letter: 'M', text: 'la personne est malade' },
    B: { letter: 'T', text: 'le test est positif' },
    params: { a: 1, b1: 99, b2: 5 },
    names: {
      a: 'Part des malades',
      b1: 'Test positif si malade (sensibilité)',
      b2: 'Test positif si pas malade (faux positifs)',
    },
  },
  {
    id: 'factory',
    label: 'Usine',
    A: { letter: 'A', text: 'la pièce vient de la machine A (sinon, de la machine B)' },
    B: { letter: 'D', text: 'la pièce est défectueuse' },
    params: { a: 60, b1: 2, b2: 5 },
    names: {
      a: 'Part de la machine A',
      b1: 'Défectueuses parmi celles de la machine A',
      b2: 'Défectueuses parmi celles de la machine B (Ā)',
    },
  },
  {
    id: 'free',
    label: 'Libre',
    A: { letter: 'A', text: 'A est réalisé' },
    B: { letter: 'B', text: 'B est réalisé' },
    params: { a: 30, b1: 70, b2: 20 },
    names: { a: 'P(A)', b1: 'B sachant A', b2: 'B sachant non A' },
    simpleNames: { a: 'P(A)', b1: 'B après A', b2: 'B après non A' },
  },
];

export const POPULATION = 10_000;

/** Les quatre chemins (A∩B, A∩B̄, Ā∩B, Ā∩B̄), en dix-millièmes exacts. */
export function joint(p: BayesParams): {
  AB: number;
  AnotB: number;
  notAB: number;
  notAnotB: number;
} {
  return {
    AB: p.a * p.b1,
    AnotB: p.a * (100 - p.b1),
    notAB: (100 - p.a) * p.b2,
    notAnotB: (100 - p.a) * (100 - p.b2),
  };
}

const exactOf = (tenThousandths: number) => rational(tenThousandths, POPULATION);

export interface BayesResults {
  /** P(A ∩ B) */
  pAB: ExactNumber;
  /** P(B), formule des probabilités totales */
  pB: ExactNumber;
  /** P_B(A), null si P(B) = 0 */
  pAgivenB: ExactNumber | null;
  /** P_B̄(A), null si P(B̄) = 0 */
  pAgivenNotB: ExactNumber | null;
  /** A et B indépendants (P_A(B) = P_Ā(B)) */
  independent: boolean;
}

export function results(p: BayesParams): BayesResults {
  const j = joint(p);
  const b = j.AB + j.notAB;
  const notB = j.AnotB + j.notAnotB;
  return {
    pAB: exactOf(j.AB),
    pB: exactOf(b),
    pAgivenB: b === 0 ? null : rational(j.AB, b),
    pAgivenNotB: notB === 0 ? null : rational(j.AnotB, notB),
    independent: p.b1 === p.b2,
  };
}

/** « 16,7 % » (une décimale, sans zéro inutile) */
export function percent(v: number): string {
  const r = Math.round(v * 1000) / 10;
  return `${String(r).replace('.', ',')} %`;
}

// ---------------------------------------------------------------------------
// Simulation dans le carré : un point au hasard = un individu

export interface SimPoint {
  x: number;
  y: number;
  A: boolean;
  B: boolean;
}

/**
 * `count` individus tirés uniformément dans le carré unité : A si x < P(A),
 * puis B si y < P_A(B) (colonne A) ou y < P_Ā(B) (colonne Ā). Même loi que
 * l'expérience en deux temps.
 */
export function simulate(p: BayesParams, count: number, rng: () => number): SimPoint[] {
  const out: SimPoint[] = [];
  for (let i = 0; i < count; i++) {
    const x = rng();
    const y = rng();
    const A = x < p.a / 100;
    const B = y < (A ? p.b1 : p.b2) / 100;
    out.push({ x, y, A, B });
  }
  return out;
}

export function simCounts(points: readonly SimPoint[]): { B: number; AB: number } {
  let B = 0;
  let AB = 0;
  for (const pt of points) {
    if (pt.B) {
      B++;
      if (pt.A) AB++;
    }
  }
  return { B, AB };
}

/** Choix du « devine d'abord » (dépistage) : la réponse est la plus petite. */
export const SCREENING_GUESSES = [
  { value: 99, label: 'Environ 99 %' },
  { value: 95, label: 'Environ 95 %' },
  { value: 50, label: 'Environ 50 %' },
  { value: 17, label: 'Environ 17 %' },
] as const;

/** Choix du « devine d'abord » le plus proche de la vraie valeur (en %). */
export function closestGuess(actualPercent: number): number {
  let best: number = SCREENING_GUESSES[0].value;
  for (const g of SCREENING_GUESSES)
    if (Math.abs(g.value - actualPercent) < Math.abs(best - actualPercent)) best = g.value;
  return best;
}
